/* eslint-disable camelcase */
import { links } from "%/enums/link-enums";
import { useGeneralStore } from "%/stores/general-store";
import { createPasskey } from "%/utils/webauthn";
import { $axios } from "@bloglovin/vue-framework";
import { defineStore } from "pinia";

export const useSecurityStore = defineStore("security-store", {
  state: () => ({
    isLoading: false,
    isLoaded: false,
    errorMessage: "",
    totpEnabled: false,
    passkeys: [],
    bypassCodesRemaining: 0,
    mfaRequired: false,
    canRemoveFactor: true,
    enrolledMethods: [],
    totpEnrollSecret: "",
    totpEnrollOtpauthUri: "",
    totpEnrollQrDataUri: "",
    generatedBypassCodes: [],
  }),
  getters: {
    usersApiV1Url() {
      return useGeneralStore().getLink(links.usersApiV1);
    },
    userId() {
      return useGeneralStore().userId;
    },
    mfaUserUrl() {
      return `${this.usersApiV1Url}/mfa/user/${this.userId}`;
    },
    // Registration goes through creator so the rp id and origin come from its config, not the browser.
    passkeyRegistrationUrl() {
      return `${useGeneralStore().apiV1}/user/mfa/passkey`;
    },
  },
  actions: {
    fetchStatus() {
      this.isLoading = true;
      return $axios
          .get(`${this.mfaUserUrl}/status`)
          .then(response => {
            this.totpEnabled = response.data.totp_enabled;
            this.passkeys = response.data.passkeys ?? [];
            this.bypassCodesRemaining = response.data.bypass_codes_remaining ?? 0;
            this.mfaRequired = response.data.mfa_required ?? false;
            this.canRemoveFactor = response.data.can_remove_factor ?? true;
            this.enrolledMethods = response.data.enrolled_methods ?? [];
            this.isLoaded = true;
          })
          .catch(error => {
            this.errorMessage = this.parseError(error);
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    beginTotpEnroll() {
      this.isLoading = true;
      this.errorMessage = "";
      return $axios
          .post(`${this.mfaUserUrl}/totp/enroll`, {})
          .then(response => {
            this.totpEnrollSecret = response.data.secret;
            this.totpEnrollOtpauthUri = response.data.otpauth_uri;
            this.totpEnrollQrDataUri = response.data.qr_svg_data_uri;
          })
          .catch(error => {
            this.errorMessage = this.parseError(error);
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    confirmTotp(code) {
      this.isLoading = true;
      this.errorMessage = "";
      return $axios
          .post(`${this.mfaUserUrl}/totp/confirm`, { code })
          .then(() => {
            this.totpEnrollSecret = "";
            this.totpEnrollOtpauthUri = "";
            this.totpEnrollQrDataUri = "";
            return this.fetchStatus();
          })
          .catch(error => {
            this.errorMessage = this.parseError(error);
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    disableTotp() {
      this.isLoading = true;
      this.errorMessage = "";
      return $axios
          .post(`${this.mfaUserUrl}/totp/disable`, {})
          .then(() => this.fetchStatus())
          .catch(error => {
            this.errorMessage = this.parseError(error);
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    async addPasskey(alias) {
      this.isLoading = true;
      this.errorMessage = "";
      try {
        const optionsResponse = await $axios.post(`${this.passkeyRegistrationUrl}/register-options`, {});
        const attestation = await createPasskey(optionsResponse.data.public_key);
        await $axios.post(`${this.passkeyRegistrationUrl}/register`, {
          registration_token: optionsResponse.data.registration_token,
          alias,
          ...attestation,
        });
        await this.fetchStatus();
      } catch (error) {
        this.errorMessage = error?.response ? this.parseError(error) : error?.message ?? "Could not add passkey.";
      } finally {
        this.isLoading = false;
      }
    },

    renamePasskey(passkeyId, alias) {
      this.isLoading = true;
      this.errorMessage = "";
      return $axios
          .post(`${this.mfaUserUrl}/passkey/${passkeyId}/rename`, { alias })
          .then(() => this.fetchStatus())
          .catch(error => {
            this.errorMessage = this.parseError(error);
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    removePasskey(passkeyId) {
      this.isLoading = true;
      this.errorMessage = "";
      return $axios
          .post(`${this.mfaUserUrl}/passkey/${passkeyId}/remove`, {})
          .then(() => this.fetchStatus())
          .catch(error => {
            this.errorMessage = this.parseError(error);
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    generateBypassCodes() {
      this.isLoading = true;
      this.errorMessage = "";
      return $axios
          .post(`${this.mfaUserUrl}/bypass-codes/generate`, {})
          .then(response => {
            this.generatedBypassCodes = response.data.codes ?? [];
            return this.fetchStatus();
          })
          .catch(error => {
            this.errorMessage = this.parseError(error);
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    clearGeneratedBypassCodes() {
      this.generatedBypassCodes = [];
    },

    parseError(error) {
      return error?.response?.data?.message ?? "Something went wrong, please try again.";
    },
  },
});
